import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Empty, Spin, Select } from 'antd';
import { cloneDeep } from 'lodash';
import { HighMedLowColor } from '../components/HighMedLowColor';
import { CaretRightOutlined } from '@ant-design/icons';
import TableView from './TableView';
import styled, {
  ScoreExplainablityContainer,
  LoadingWrapper,
  PreventionContainer
} from './styled';
import PanelExtraTemplate from '../components/PanelExtraTemplate';
import SwitchCumToggal from '../../common/components/SwitchCumToggal';
import hardRuleFeather from '../../../assets/hardRuleFeather.svg';
import nonHardRule from '../../../assets/nonHardRule.svg';
import PreventionTable from './preventionTable';
import style from './index.scss';
import { hasAttributeValue } from '../../../util';
import { getSelectedResources } from '../../../util/resourceManager';

const { plrtb, styleCustomPanel, styleHighMedLow, panelHeading } = styled;
const { Panel } = Collapse;
const { trmResources } = getSelectedResources();

const ScoreExplanability = ({
  authentication,
  scoreExplainability,
  apiActions,
  scoreExplainabilityPrevention,
  getAMLSCommonAlertPartyIdentity,
  alertPartyIdentity,
  unifiedAlerts,
  isPrevention,
  trmActions,
  ...props
}) => {
  const {
    alertDetails: { data }
  } = unifiedAlerts;
  const {
    getScoreExplainablity,
    getAmlsCommonPreventionScoreExplainablity,
    fetchTypologiesActions
  } = apiActions;
  const [newDataSource, setNewDataSource] = useState([]);
  const [switchStatus, setSwitchStatus] = useState(true);
  const { alertId, isLoading } = props;
  const [rules, setRules] = useState([]);
  const [dropdownRules, setDropdownRules] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState({
    rule: '',
    role: ''
  });

  const preventionResourceAccess = hasAttributeValue(
    authentication,
    trmResources.APP_3_RESOURCE_UI_RUN_INSTANCE,
    '24'
  );

  useEffect(() => {
    if (isPrevention) {
      getAmlsCommonPreventionScoreExplainablity({ alertId: alertId });
    } else {
      getScoreExplainablity({ alertId });
    }
  }, [alertId]);

  useEffect(() => {
    if (preventionResourceAccess && !trmActions?.data?.length) {
      fetchTypologiesActions();
    }
  }, [preventionResourceAccess]);

  useEffect(() => {
    const dataSource =
      switchStatus &&
      cloneDeep(scoreExplainability)?.map((records) => {
        records.riskIndicators = records?.riskIndicators.filter(
          ({ importance }) => !!importance
        );
        return records;
      });

    setNewDataSource(switchStatus ? dataSource : scoreExplainability);
  }, [scoreExplainability, switchStatus]);

  useEffect(() => {
    // The rules dropdown options constructed from score explainability data.
    // So set the options when rule and role is all otherwise do nothing.
    if (
      !selectedFilter.rule &&
      !selectedFilter.role &&
      scoreExplainabilityPrevention
    ) {
      const uniqueEntityRules = new Set();
      Object.keys(scoreExplainabilityPrevention).forEach((typologyName) => {
        const typologiesForName = scoreExplainabilityPrevention[typologyName];
        typologiesForName.forEach((typology) => {
          const { triggerName } = typology;
          if (!uniqueEntityRules.has(triggerName)) {
            uniqueEntityRules.add(triggerName);
          }
        });
      });
      setRules(Array.from(uniqueEntityRules));
    }
  }, [scoreExplainabilityPrevention]);

  const setRolesState = (state) => {
    // The roles dropdown options constructed from score explainability data.
    // So set the options when role and rule is all otherwise do nothing.
    if (!selectedFilter.role && !selectedFilter.rule) {
      const uniqueState = Array.from(new Set(state));
      setDropdownRules(uniqueState);
    }
  };

  const filterChange = (filters) => {
    const params = { alertId };

    if (filters.role) {
      params['entityRole'] = filters.role;
    }

    if (filters.rule) {
      params['ruleName'] = filters.rule;
    }

    setSelectedFilter(filters);
    getAmlsCommonPreventionScoreExplainablity(params);
  };

  const ruleOptions = useMemo(
    () => [
      { value: '', label: 'All' }, // Add the 'All' option
      ...rules.map((rule) => ({ value: rule, label: rule })) // Add other rule options
    ],
    [rules]
  );

  const roleOptions = useMemo(
    () => [
      { value: '', label: 'All' }, // Add the 'All' option
      ...dropdownRules.map((role) => ({ value: role, label: role })) // Add other role options
    ],
    [dropdownRules]
  );

  return isPrevention ? (
    <ScoreExplainablityContainer>
      <PreventionContainer>
        <div className={style.PreventionContainer}>
          <header
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              paddingBottom: '10px'
            }}>
            {/* <b className="mb-3"> Details </b> */}
            <div
              style={{
                display: 'flex',
                gap: '30px',
                width: '100%',
                justifyContent: 'flex-end'
              }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label>{`Group By`}</label>
                <Select
                  style={{ width: 350 }}
                  defaultValue=""
                  onChange={(role) => filterChange({ ...selectedFilter, role })}
                  options={roleOptions}
                  placeholder="Please Select Grouping"
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label>{`Variant Name`}</label>
                <Select
                  style={{ width: 350 }}
                  defaultValue=""
                  onChange={(rule) => filterChange({ ...selectedFilter, rule })}
                  options={ruleOptions}
                  placeholder="Please Select Variant"
                />
              </div>
            </div>
          </header>
          <PreventionTable
            scoreExplainabilityData={scoreExplainabilityPrevention}
            setRolesState={setRolesState}
            alertInfo={data}
            trmActions={trmActions}
            selectedFilter={selectedFilter}
            isLoading={isLoading}
          />
        </div>
      </PreventionContainer>
    </ScoreExplainablityContainer>
  ) : (
    <ScoreExplainablityContainer>
      <header style={plrtb}>
        <b className="mb-3"> Score Explanation </b>

        <div style={styleHighMedLow}>
          <SwitchCumToggal
            onChange={(status) => setSwitchStatus(status)}
            defaultValue={switchStatus}
            onText="Show non-contributing indicators"
            offText="Hide non-contributing indicators"
          />
          <HighMedLowColor />
        </div>
      </header>
      <Collapse
        style={{ backgroundColor: '#fff' }}
        defaultActiveKey={['1']}
        bordered={false}
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}>
        {newDataSource?.length ? (
          newDataSource.map((item, index) => (
            <Panel
              header={
                <span style={panelHeading}>
                  <img src={item.isHardRule ? hardRuleFeather : nonHardRule} />{' '}
                  &nbsp; {item?.name}
                </span>
              }
              key={index + 1}
              extra={PanelExtraTemplate(item)}
              style={styleCustomPanel}>
              <TableView
                tableData={item?.riskIndicators}
                enableClusterMean={item.enableClusterMean || false}
                mandatoryScoringCondition={item.conditionForMandatoryScoring}
                authentication={authentication}
              />
            </Panel>
          ))
        ) : (
          <LoadingWrapper>
            {isLoading ? (
              <Spin size="large" />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </LoadingWrapper>
        )}
      </Collapse>
    </ScoreExplainablityContainer>
  );
};

export default ScoreExplanability;
